import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import {
  parseMatrixCellKey,
  prefillBufferFromVerticals,
  type TaxonomyIndex,
} from "../offeredServices/catalogueModel.js";
import {
  initialOnboardingFlowWizardState,
  type OfferedServicesCommitPath,
  type OfferedServicesPayload,
  type OnboardingFlowWizardAction,
  type OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

function toggleId(arr: string[], id: string): string[] {
  return arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
}

function toggleProjectType(
  arr: OfferedProjectType[],
  projectType: OfferedProjectType
): OfferedProjectType[] {
  return arr.includes(projectType)
    ? arr.filter(x => x !== projectType)
    : [...arr, projectType];
}

const discardedMatrix = (): Pick<
  OnboardingFlowWizardState,
  "applyToAll" | "matrixDeclaredCategories" | "matrixSelections"
> => ({
  applyToAll: "yes",
  matrixDeclaredCategories: [],
  matrixSelections: [],
});

export function onboardingFlowWizardReducer(
  state: OnboardingFlowWizardState,
  action: OnboardingFlowWizardAction
): OnboardingFlowWizardState {
  switch (action.type) {
    case "toggleVertical":
      // Changing verticals invalidates a customised matrix (its rows and cells
      // were derived from the old vertical set): back to the chips.
      return {
        ...state,
        ...discardedMatrix(),
        selectedVerticals: toggleId(state.selectedVerticals, action.categoryId),
      };

    case "toggleProjectType": {
      const turnedOn = !state.selectedProjectTypes.includes(action.projectType);
      const selectedProjectTypes = toggleProjectType(
        state.selectedProjectTypes,
        action.projectType
      );
      if (state.applyToAll === "yes") {
        return { ...state, selectedProjectTypes };
      }
      // Customised: the chip also sets or clears the type on the declared
      // leaves, so the chips and the matrix never contradict each other.
      const leafIds = new Set(action.declaredLeafIds);
      const matrixSelections = turnedOn
        ? [
            ...state.matrixSelections,
            ...action.declaredLeafIds
              .filter(
                categoryId =>
                  !state.matrixSelections.some(
                    s =>
                      s.categoryId === categoryId &&
                      s.projectType === action.projectType
                  )
              )
              .map(categoryId => ({
                categoryId,
                projectType: action.projectType,
              })),
          ]
        : state.matrixSelections.filter(
            s =>
              !(
                leafIds.has(s.categoryId) &&
                s.projectType === action.projectType
              )
          );
      return { ...state, selectedProjectTypes, matrixSelections };
    }

    case "applyMatrix":
      // The modal's grid owns the draft; store it verbatim (including any
      // session-memory cells for undeclared categories). The commit selector
      // filters selections to declared rows.
      return {
        ...state,
        applyToAll: "no",
        matrixDeclaredCategories: [...action.declaredCategories],
        matrixSelections: [...action.selections],
      };

    case "resetCustomisation":
      return { ...state, ...discardedMatrix() };

    case "reset":
      return initialOnboardingFlowWizardState;
  }
}

// Path -> payload selector. Maps the wizard state + the chosen commit path
// to the exact { categories, services } shape consumed by
// setOrganisationOfferedServices.
export function selectOfferedServicesPayload(
  state: OnboardingFlowWizardState,
  path: OfferedServicesCommitPath,
  index: TaxonomyIndex
): OfferedServicesPayload {
  switch (path) {
    case "all-services": {
      // Cascade the picked work types down each vertical's subtree onto its
      // leaves. A flat vertical x projectType cross-product would write
      // services on non-leaf verticals, which the leaf-only catalogue never
      // renders (they would appear unapplied).
      const buffer = prefillBufferFromVerticals(
        index,
        state.selectedVerticals,
        state.selectedProjectTypes
      );
      return {
        categories: [...buffer.declared],
        services: [...buffer.selections].map(parseMatrixCellKey),
      };
    }

    case "customised": {
      // `categories` is the declared-row set from the grid (already includes
      // forced-active ancestors). Selections may retain session-memory cells
      // for undeclared categories, so filter to declared rows - only
      // active-row services are committed.
      const declaredSet = new Set(state.matrixDeclaredCategories);
      return {
        categories: [...state.matrixDeclaredCategories],
        services: state.matrixSelections
          .filter(s => declaredSet.has(s.categoryId))
          .map(s => ({
            categoryId: s.categoryId,
            projectType: s.projectType,
          })),
      };
    }
  }
}
